import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '../../contexts/AuthContext'
import { dataSourcesService, DataSource, FirestoreConfig } from '../../services/dataSourcesService'
import { 
  Database, 
  ChevronRight, 
  Loader2, 
  AlertCircle,
  CheckCircle2,
  Folder,
  BarChart3
} from 'lucide-react'

interface DataSourceSelectorProps {
  onDataSelected: (data: any[], collectionName: string, dataSourceName: string) => void
  onClose?: () => void
}

interface CollectionPreview {
  name: string
  sampleData: any[]
  recordCount: number
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({ onDataSelected, onClose }) => {
  const [dataSources, setDataSources] = useState<DataSource[]>([])
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null)
  const [collections, setCollections] = useState<string[]>([])
  const [collectionPreviews, setCollectionPreviews] = useState<Record<string, CollectionPreview>>({})
  const [isLoadingDataSources, setIsLoadingDataSources] = useState(true)
  const [isLoadingCollections, setIsLoadingCollections] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState<string | null>(null)
  const { currentUser } = useAuth()
  const { toast } = useToast()

  // For now, use user ID as organization ID - replace with actual organization logic
  const organizationId = currentUser?.uid || 'default-org'

  useEffect(() => {
    loadDataSources()
  }, [])

  const loadDataSources = async () => {
    try {
      setIsLoadingDataSources(true)
      const response = await dataSourcesService.getUserDataSources(organizationId)
      
      // Filter only connected Firestore sources
      const connectedFirestoreSources = response.dataSources.filter(
        ds => ds.type === 'firestore' && ds.status === 'connected'
      )
      
      setDataSources(connectedFirestoreSources)
      
      if (connectedFirestoreSources.length === 0) {
        toast({
          title: "No Connected Data Sources",
          description: "Connect a Firestore data source first to use this feature.",
          variant: "destructive",
        })
      }
    } catch (error: any) {
      console.error('Error loading data sources:', error)
      toast({
        title: "Error Loading Data Sources",
        description: error.message || "Failed to load your connected data sources.",
        variant: "destructive",
      })
    } finally {
      setIsLoadingDataSources(false)
    }
  }

  const loadCollections = async (dataSource: DataSource) => {
    try {
      setIsLoadingCollections(true)
      setSelectedDataSource(dataSource)
      setCollections([])
      setCollectionPreviews({})
      
      const response = await dataSourcesService.getCollections(dataSource.id)
      setCollections(response.collections)
      
      // Load previews for first few collections
      const previewPromises = response.collections.slice(0, 5).map(async (collectionName) => {
        try {
          const sampleResponse = await dataSourcesService.getSampleData(dataSource.id, collectionName, 3)
          return {
            name: collectionName,
            preview: {
              name: collectionName,
              sampleData: sampleResponse.sampleData,
              recordCount: sampleResponse.documentCount
            }
          }
        } catch (error) {
          return {
            name: collectionName,
            preview: {
              name: collectionName,
              sampleData: [],
              recordCount: 0
            }
          }
        }
      })
      
      const previews = await Promise.all(previewPromises)
      const previewMap: Record<string, CollectionPreview> = {}
      previews.forEach(({ name, preview }) => {
        previewMap[name] = preview
      })
      setCollectionPreviews(previewMap)
      
    } catch (error: any) {
      console.error('Error loading collections:', error)
      toast({
        title: "Error Loading Collections",
        description: error.message || "Failed to load collections from this data source.",
        variant: "destructive",
      })
    } finally {
      setIsLoadingCollections(false)
    }
  }

  const selectCollection = async (collectionName: string) => {
    if (!selectedDataSource) return
    
    try {
      setIsLoadingPreview(collectionName)
      
      // Get a larger sample for chart creation (50 records)
      const response = await dataSourcesService.getSampleData(selectedDataSource.id, collectionName, 50)
      
      if (response.sampleData.length === 0) {
        toast({
          title: "No Data Found",
          description: `The collection "${collectionName}" appears to be empty.`,
          variant: "destructive",
        })
        return
      }
      
      // Pass the data back to the parent component
      onDataSelected(response.sampleData, collectionName, selectedDataSource.name)
      
      toast({
        title: "Data Loaded Successfully",
        description: `Loaded ${response.sampleData.length} records from "${collectionName}".`,
        variant: "success",
      })
      
    } catch (error: any) {
      console.error('Error loading collection data:', error)
      toast({
        title: "Error Loading Data",
        description: error.message || `Failed to load data from "${collectionName}".`,
        variant: "destructive",
      })
    } finally {
      setIsLoadingPreview(null)
    }
  }

  const renderDataSourceCard = (dataSource: DataSource) => (
    <Card 
      key={dataSource.id} 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => loadCollections(dataSource)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base font-medium">{dataSource.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-xs">
                  {(dataSource.configuration as FirestoreConfig).projectId}
                </Badge>
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              </div>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Firestore database with {(dataSource.configuration as FirestoreConfig).collections?.length || 0} collections
        </p>
      </CardContent>
    </Card>
  )

  const renderCollectionCard = (collectionName: string) => {
    const preview = collectionPreviews[collectionName]
    const isLoading = isLoadingPreview === collectionName
    
    return (
      <Card 
        key={collectionName}
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => !isLoading && selectCollection(collectionName)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Folder className="h-4 w-4 text-orange-500" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium">{collectionName}</CardTitle>
                {preview && (
                  <p className="text-xs text-muted-foreground">
                    {preview.recordCount > 0 ? `${preview.recordCount}+ records` : 'Empty collection'}
                  </p>
                )}
              </div>
            </div>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {preview && preview.sampleData.length > 0 && (
          <CardContent>
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">Sample fields:</p>
              <div className="flex flex-wrap gap-1">
                {Object.keys(preview.sampleData[0] || {}).slice(0, 4).map((field) => (
                  <Badge key={field} variant="outline" className="text-xs">
                    {field}
                  </Badge>
                ))}
                {Object.keys(preview.sampleData[0] || {}).length > 4 && (
                  <Badge variant="outline" className="text-xs">
                    +{Object.keys(preview.sampleData[0] || {}).length - 4} more
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    )
  }

  if (isLoadingDataSources) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Database className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Choose Data Source</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading data sources...</span>
        </div>
      </div>
    )
  }

  if (dataSources.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Connected Data Sources</h3>
        <p className="text-muted-foreground mb-4">
          You need to connect a Firestore data source first.
        </p>
        <Button onClick={onClose} variant="outline">
          Go to Data Sources
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!selectedDataSource ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Choose Data Source</h3>
          </div>
          <div className="space-y-3">
            {dataSources.map(renderDataSourceCard)}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setSelectedDataSource(null)
                setCollections([])
                setCollectionPreviews({})
              }}
            >
              ← Back to Data Sources
            </Button>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            <Folder className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-semibold">Choose Collection</h3>
            <Badge variant="secondary">{selectedDataSource.name}</Badge>
          </div>
          
          {isLoadingCollections ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading collections...</span>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {collections.map(renderCollectionCard)}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default DataSourceSelector 